/**
 * Analytics Service
 * Provides a typed interface for tracking events and managing analytics state
 */

import posthog from 'posthog-js';
import type {
  AnalyticsEvent,
  EventProperties,
  AnalyticsUserProperties,
  FeatureFlagValue
} from '../types/analytics';
import { sanitizeProperties, sanitizeUrl } from '../utils/analytics-masking';

/** Check if PostHog is initialized and enabled */
function isPostHogReady(): boolean {
  try {
    // PostHog sets __loaded when initialized
    return posthog && typeof posthog.capture === 'function' && (posthog as any).__loaded === true;
  } catch {
    return false;
  }
}

/**
 * Track a custom analytics event
 * @param event - The event name from AnalyticsEvent union
 * @param properties - Optional event properties (will be sanitized)
 */
export function trackEvent(
  event: AnalyticsEvent,
  properties?: EventProperties
): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    const sanitizedProps = properties ? sanitizeProperties(properties) : {};

    // Add timestamp if not present
    if (!sanitizedProps.timestamp) {
      sanitizedProps.timestamp = new Date().toISOString();
    }

    posthog.capture(event, sanitizedProps);
  } catch (error) {
    // Silently fail - analytics should never break the app
    console.warn('[Analytics] Failed to track event:', event, error);
  }
}

/**
 * Track a page view event
 * @param pageName - Human-readable page name
 * @param route - The route path (will be sanitized)
 */
export function trackPageView(pageName: string, route?: string): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    const sanitizedRoute = route ? sanitizeUrl(route) : undefined;

    posthog.capture('$pageview', {
      page_name: pageName,
      $current_url: sanitizedRoute
    });
  } catch (error) {
    console.warn('[Analytics] Failed to track page view:', error);
  }
}

/**
 * Identify a user and set their properties
 * @param userId - Unique user identifier (internal operator ID)
 * @param properties - User properties (low cardinality only)
 */
export function identifyUser(
  userId: string,
  properties?: AnalyticsUserProperties
): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    // Use a hashed/anonymized ID if preferred for privacy
    posthog.identify(userId, properties);
  } catch (error) {
    console.warn('[Analytics] Failed to identify user:', error);
  }
}

/**
 * Update user properties without changing identity
 * @param properties - Properties to set/update
 */
export function setUserProperties(properties: AnalyticsUserProperties): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    posthog.people.set(properties);
  } catch (error) {
    console.warn('[Analytics] Failed to set user properties:', error);
  }
}

/**
 * Reset user identity (call on logout)
 */
export function resetUser(): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    posthog.reset();
  } catch (error) {
    console.warn('[Analytics] Failed to reset user:', error);
  }
}

/**
 * Get a feature flag value
 * @param flagKey - The feature flag key
 * @param defaultValue - Default value if flag not found
 * @returns The flag value or default
 */
export function getFeatureFlag<T extends FeatureFlagValue = boolean>(
  flagKey: string,
  defaultValue: T
): T {
  if (!isPostHogReady()) {
    return defaultValue;
  }

  try {
    const value = posthog.getFeatureFlag(flagKey);
    if (value === undefined || value === null) {
      return defaultValue;
    }
    return value as T;
  } catch (error) {
    console.warn('[Analytics] Failed to get feature flag:', flagKey, error);
    return defaultValue;
  }
}

/**
 * Check if a feature flag is enabled (boolean flags)
 * @param flagKey - The feature flag key
 * @returns Whether the flag is enabled
 */
export function isFeatureEnabled(flagKey: string): boolean {
  return getFeatureFlag(flagKey, false) === true;
}

/**
 * Get feature flag payload (for multivariate flags)
 * @param flagKey - The feature flag key
 * @returns The payload or undefined
 */
export function getFeatureFlagPayload<T = Record<string, unknown>>(
  flagKey: string
): T | undefined {
  if (!isPostHogReady()) {
    return undefined;
  }

  try {
    return posthog.getFeatureFlagPayload(flagKey) as T | undefined;
  } catch (error) {
    console.warn('[Analytics] Failed to get feature flag payload:', flagKey, error);
    return undefined;
  }
}

/**
 * Manually trigger a feature flag reload
 */
export function reloadFeatureFlags(): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    posthog.reloadFeatureFlags();
  } catch (error) {
    console.warn('[Analytics] Failed to reload feature flags:', error);
  }
}

/**
 * Capture an exception/error
 * @param error - The error object
 * @param context - Additional context about where the error occurred
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>
): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    const sanitizedContext = context ? sanitizeProperties(context) : {};

    posthog.capture('$exception', {
      $exception_message: error.message,
      $exception_type: error.name,
      $exception_stack: error.stack,
      ...sanitizedContext
    });
  } catch (captureError) {
    console.warn('[Analytics] Failed to capture exception:', captureError);
  }
}

/**
 * Start a session recording manually (if not auto-started)
 */
export function startSessionRecording(): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    posthog.startSessionRecording();
  } catch (error) {
    console.warn('[Analytics] Failed to start session recording:', error);
  }
}

/**
 * Stop session recording
 */
export function stopSessionRecording(): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    posthog.stopSessionRecording();
  } catch (error) {
    console.warn('[Analytics] Failed to stop session recording:', error);
  }
}

/**
 * Check if session recording is active
 */
export function isSessionRecordingActive(): boolean {
  if (!isPostHogReady()) {
    return false;
  }

  try {
    return posthog.sessionRecordingStarted?.() ?? false;
  } catch {
    return false;
  }
}

/**
 * Opt user out of tracking
 */
export function optOut(): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    posthog.opt_out_capturing();
  } catch (error) {
    console.warn('[Analytics] Failed to opt out:', error);
  }
}

/**
 * Opt user back in to tracking
 */
export function optIn(): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    posthog.opt_in_capturing();
  } catch (error) {
    console.warn('[Analytics] Failed to opt in:', error);
  }
}

/**
 * Check if user has opted out
 */
export function hasOptedOut(): boolean {
  if (!isPostHogReady()) {
    return false;
  }

  try {
    return posthog.has_opted_out_capturing?.() ?? false;
  } catch {
    return false;
  }
}

/**
 * Register super properties (included with every event)
 * @param properties - Properties to register
 */
export function registerSuperProperties(properties: Record<string, unknown>): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    const sanitized = sanitizeProperties(properties);
    posthog.register(sanitized);
  } catch (error) {
    console.warn('[Analytics] Failed to register super properties:', error);
  }
}

/**
 * Unregister a super property
 * @param propertyName - The property to unregister
 */
export function unregisterSuperProperty(propertyName: string): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    posthog.unregister(propertyName);
  } catch (error) {
    console.warn('[Analytics] Failed to unregister super property:', error);
  }
}

// =============================================================================
// GROUP ANALYTICS
// =============================================================================

import type { VendorGroupProperties, GroupType } from '../types/analytics';

/**
 * Set the vendor group for the current user
 * This enables vendor-level analytics and segmentation
 * @param vendorId - The vendor ID (will be used as group key)
 * @param properties - Optional vendor properties (low cardinality)
 */
export function setVendorGroup(
  vendorId: string,
  properties?: VendorGroupProperties
): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    posthog.group('vendor', vendorId, properties);
  } catch (error) {
    console.warn('[Analytics] Failed to set vendor group:', error);
  }
}

/**
 * Set a custom group for the current user
 * @param groupType - The type of group (e.g., 'vendor', 'organization', 'team')
 * @param groupKey - The unique identifier for the group
 * @param properties - Optional group properties
 */
export function setGroup(
  groupType: GroupType,
  groupKey: string,
  properties?: Record<string, unknown>
): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    const sanitizedProps = properties ? sanitizeProperties(properties) : undefined;
    posthog.group(groupType, groupKey, sanitizedProps);
  } catch (error) {
    console.warn('[Analytics] Failed to set group:', groupType, error);
  }
}

/**
 * Track an event with explicit group association
 * Use this when you need to attribute an event to a specific group
 * @param event - The event name
 * @param groupType - The group type to associate
 * @param groupKey - The group key to associate
 * @param properties - Optional event properties
 */
export function trackGroupEvent(
  event: AnalyticsEvent,
  groupType: GroupType,
  groupKey: string,
  properties?: EventProperties
): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    const sanitizedProps = properties ? sanitizeProperties(properties) : {};

    posthog.capture(event, {
      ...sanitizedProps,
      $groups: { [groupType]: groupKey }
    });
  } catch (error) {
    console.warn('[Analytics] Failed to track group event:', event, error);
  }
}

/**
 * Update properties for a group without re-identifying
 * @param groupType - The group type
 * @param groupKey - The group key
 * @param properties - Properties to set/update
 */
export function setGroupProperties(
  groupType: GroupType,
  groupKey: string,
  properties: Record<string, unknown>
): void {
  if (!isPostHogReady()) {
    return;
  }

  try {
    const sanitizedProps = sanitizeProperties(properties);
    // Re-calling group with properties updates them
    posthog.group(groupType, groupKey, sanitizedProps);
  } catch (error) {
    console.warn('[Analytics] Failed to set group properties:', error);
  }
}
